/**
 * Bidirectional path segment translation map for DE ↔ EN routes.
 * 
 * All internal links use German paths (e.g. localizePath("/kontakt")).
 * When language is EN, localizePath() auto-translates segments using this map.
 * 
 * Keys: German path segments → Values: English path segments
 */
export const deToEnSegments: Record<string, string> = {
  produkte: "products",
  branchen: "industries",
  loesungen: "solutions",
  preise: "pricing",
  integrationen: "integrations",
  "ueber-uns": "about",
  karriere: "careers",
  kontakt: "contact",
  nutzungsbedingungen: "terms",
  datenschutzrichtlinie: "privacy",
  impressum: "imprint",
  "loesungspartner-programm": "solution-partner",
};

// Reverse map: EN → DE
export const enToDeSegments: Record<string, string> = Object.fromEntries(
  Object.entries(deToEnSegments).map(([de, en]) => [en, de])
);

/**
 * Translate a German path to its English equivalent.
 * Only translates known top-level segments; dynamic slugs pass through.
 * e.g. "/produkte/ai-agent" → "/products/ai-agent"
 *      "/kontakt" → "/contact"
 */
export function translatePathToEn(path: string): string {
  const segments = path.split("/");
  return segments
    .map((seg, i) => {
      // Only translate the first meaningful segment (index 1) and possibly index 2
      // for compound German slugs like "loesungspartner-programm"
      if (i >= 1 && i <= 1) {
        return deToEnSegments[seg] ?? seg;
      }
      return seg;
    })
    .join("/");
}

/**
 * Translate an English path back to its German base equivalent.
 * e.g. "/products/ai-agent" → "/produkte/ai-agent"
 */
export function translatePathToDe(path: string): string {
  const segments = path.split("/");
  return segments
    .map((seg, i) => {
      if (i >= 1 && i <= 1) {
        return enToDeSegments[seg] ?? seg;
      }
      return seg;
    })
    .join("/");
}
